import { ReactNode, useCallback, useEffect, useRef, useState } from 'react'

import * as ToggleGroup from '@radix-ui/react-toggle-group'
import { twMerge } from 'tailwind-merge'
import { v4 as uuid } from 'uuid'

import HStack from '../Flex/HStack'
import FormControl from '../FormControl/FormControl'
import { FormControlProps } from '../FormControl/util'
import Icon from '../Icon/Icon'
import { IconKey } from '../Icon/util'
import { T } from '../Text/Text'

type SegmentedControlBaseProps<TValue extends string> = FormControlProps & {
  children: ReactNode
  className?: string
  onValueChange: (value: TValue) => void
  value: TValue
}

type SegmentedControlItemProps<TValue extends string> = {
  children: ReactNode
  className?: string
  disabled?: boolean
  value: TValue
  icon?: IconKey
}

const SegmentedControlBase = <TValue extends string>({
  ariaLabel,
  children,
  className,
  errorMessage,
  id: idProp,
  isInvalid,
  label,
  onValueChange,
  value,
  width = '100%',
}: SegmentedControlBaseProps<TValue>) => {
  const [id] = useState(idProp ?? uuid())
  const rootRef = useRef<HTMLDivElement>(null)
  const indicatorRef = useRef<HTMLDivElement>(null)

  const [isFirstRender, setIsFirstRender] = useState(true)

  // Identifies which item is selected and updates the indicator position to be right behind it
  const updateIndicatorPosition = useCallback(() => {
    const root = rootRef.current
    const indicator = indicatorRef.current
    if (!root || !indicator) {
      return
    }

    const selected = root.querySelector('[data-state="on"]') as HTMLElement | null
    if (!selected) {
      // There should always be a selected item, but just in case
      indicator.style.width = '0px'
      indicator.style.transform = 'translateX(0)'
      return
    }

    const parentRect = root.getBoundingClientRect()
    const selectedRect = selected.getBoundingClientRect()

    const borderOffset = 2 // 1px for each side; turned on box-content for the indicator so it overlaps the root container border
    const left = selectedRect.left - parentRect.left - borderOffset
    const selectedWidth = selectedRect.width

    indicator.style.width = `${selectedWidth}px`

    if (isFirstRender) {
      // Don't show an animation on the first render
      indicator.style.transition = 'transform 0s'
      indicator.style.transform = `translateX(${left}px)`
      setIsFirstRender(false)
    } else {
      // Reset the transition duration to the default
      indicator.style.transition = ''
      indicator.style.transform = `translateX(${left}px)`
    }
  }, [isFirstRender])

  const handleValueChange = useCallback(
    (next: TValue) => {
      if (next === '' || next === undefined) {
        return
      }
      onValueChange(next)
    },
    [onValueChange]
  )

  useEffect(() => {
    updateIndicatorPosition()
  }, [value, children, updateIndicatorPosition])

  useEffect(() => {
    const root = rootRef.current
    if (!root) {
      return
    }
    const ro = new ResizeObserver(() => updateIndicatorPosition())
    ro.observe(root)
    return () => ro.disconnect()
  }, [updateIndicatorPosition])

  return (
    <FormControl
      errorMessage={errorMessage}
      id={id}
      isInvalid={isInvalid}
      label={label}
      width={width}
    >
      {({ ariaDescribedBy, ariaInvalid, ariaLive }) => (
        <ToggleGroup.Root
          aria-describedby={ariaDescribedBy}
          aria-invalid={ariaInvalid}
          aria-label={ariaLabel}
          aria-live={ariaLive}
          id={id}
          onValueChange={handleValueChange}
          type="single"
          value={value}
          className={twMerge(
            'relative flex w-full items-stretch justify-center rounded-md border border-neutral-300 bg-neutral-200',
            className
          )}
          ref={rootRef}
        >
          <div
            ref={indicatorRef}
            className="pointer-events-none absolute top-[-1px] left-0 z-0 !box-content h-full rounded-md border border-neutral-300 bg-neutral-50 transition-[transform,width] duration-200 ease-out"
            aria-hidden
          />
          {children}
        </ToggleGroup.Root>
      )}
    </FormControl>
  )
}

const SegmentedControlItem = <TValue extends string>({
  children,
  icon,
  value,
  className,
  disabled,
}: SegmentedControlItemProps<TValue>) => {
  const generalFormatting =
    'group z-10 h-8 flex-1 rounded-md px-2.5 whitespace-nowrap outline-none text-neutral-950 data-[state=off]:text-neutral-600'
  const cursorFormatting = disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'
  const focusFormatting = disabled
    ? ''
    : 'data-[state=off]:hover:text-neutral-950 focus-visible:outline-2 focus-visible:outline-sky-800 focus-visible:outline-solid'
  const textFormatting = 'text-inherit'
  return (
    <ToggleGroup.Item
      value={value}
      disabled={disabled}
      className={twMerge(generalFormatting, cursorFormatting, focusFormatting, className)}
    >
      <HStack gap={2} justify="center">
        {icon && (
          <T.Body className={textFormatting}>
            <Icon icon={icon} />
          </T.Body>
        )}
        {typeof children === 'string' ? (
          <T.Body className={textFormatting}>{children}</T.Body>
        ) : (
          children
        )}
      </HStack>
    </ToggleGroup.Item>
  )
}

export default Object.assign(SegmentedControlBase, {
  Item: SegmentedControlItem,
})
